import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { he, enUS } from 'date-fns/locale';
import { Globe, Lock, ShieldAlert } from 'lucide-react';
import type { ContractStatus, DocumentClassification, QuoteStatus } from '../../types';
import Badge from '../ui/Badge';
import { TONE_BADGE, statusTone, type Tone } from '../ui/tone';
import { formatCurrency } from '../../utils/format';

/**
 * Small shared building blocks for the sales/documents screens (quotes,
 * contracts, lead conversion, customer hub, documents, portal). Kept here so
 * every screen renders the same status colours and error text.
 */

/** Human-readable API error: the interceptor rejects with the body (`err.error.message`). */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { error?: { message?: string } | string; message?: string };
    if (e.error && typeof e.error === 'object' && e.error.message) return e.error.message;
    if (typeof e.error === 'string' && e.error) return e.error;
  }
  return fallback;
}

/** True when the API rejected with HTTP 403. */
export function isForbidden(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { response?: { status?: number }; error?: { code?: number } };
  return e.response?.status === 403 || e.error?.code === 403;
}

/** ILS amount via the shared formatter (locale-aware); a missing value renders as 0. */
export function formatIls(value: number | null | undefined): string {
  const n = Number(value);
  return formatCurrency(Number.isFinite(n) ? n : 0);
}

/** Minutes → "12:30" style hours string (h:mm). */
export function formatMinutes(minutes: number | null | undefined): string {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

export function useDateFormatter() {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? enUS : he;
  return (iso: string | null | undefined, pattern = 'dd/MM/yyyy'): string => {
    if (!iso) return '—';
    try {
      return format(new Date(iso), pattern, { locale });
    } catch {
      return '—';
    }
  };
}

/** Quote status → semantic tone (DESIGN_SYSTEM.md §2.4). */
const QUOTE_STATUS_TONE: Record<QuoteStatus, Tone> = {
  draft: 'neutral',
  sent: 'info',
  viewed: 'info',
  accepted: 'success',
  rejected: 'danger',
  expired: 'warning',
  cancelled: 'neutral',
  superseded: 'neutral',
};

/** Soft tint classes for a quote status (kept for callers that style their own chip). */
export function quoteStatusClass(status: QuoteStatus): string {
  return TONE_BADGE[QUOTE_STATUS_TONE[status] ?? statusTone(status)];
}

export function QuoteStatusBadge({ status, className = '' }: { status: QuoteStatus; className?: string }) {
  const { t } = useTranslation();
  return (
    <Badge tone={QUOTE_STATUS_TONE[status] ?? statusTone(status)} dot className={className}>
      {t(`quoteStatus.${status}`)}
    </Badge>
  );
}

export function ContractStatusBadge({ status }: { status: ContractStatus }) {
  const { t } = useTranslation();
  return (
    <Badge tone={statusTone(status)} dot>
      {t(`contracts.status.${status}`)}
    </Badge>
  );
}

export function ClassificationBadge({ classification }: { classification?: DocumentClassification | null }) {
  const { t } = useTranslation();
  const c: DocumentClassification = classification ?? 'internal';
  const tone: Tone = c === 'client_visible' ? 'info' : c === 'financial_sensitive' ? 'danger' : 'neutral';
  const Icon = c === 'client_visible' ? Globe : c === 'financial_sensitive' ? ShieldAlert : Lock;
  return (
    <Badge tone={tone}>
      <Icon size={12} aria-hidden />
      {t(`documents.classification.${c}`)}
    </Badge>
  );
}

export function PublishedBadge() {
  const { t } = useTranslation();
  return (
    <Badge tone="info">
      <Globe size={12} aria-hidden />
      {t('documents.publishedBadge')}
    </Badge>
  );
}

/** Inline red error line used under forms and inside dialogs. */
export function InlineError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700" role="alert">
      {children}
    </div>
  );
}

export function Spinner() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center py-8" role="status">
      <div className="spinner" aria-hidden="true" />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}
